import { SanitizedDocument } from '../../store/documents';

function getDocumentIdAndPages(document: SanitizedDocument): [number, number[]] {
  return [document.id, document.pdfPages.map((page) => page.id)];
}

function buildHierarchyForCategory(documents: readonly SanitizedDocument[]) {
  return new Map(documents.map(getDocumentIdAndPages));
}

export type DocumentCategory = 'components' | 'metaPdfs';

export default class DocumentHierarchy {
  components: Map<number, number[]>;
  metaPdfs: Map<number, number[]>;

  private documents: Map<number, SanitizedDocument>;

  constructor(components: readonly SanitizedDocument[], metaPdfs: readonly SanitizedDocument[]) {
    Object.defineProperty(this, 'documents', { enumerable: false, writable: true });

    const allDocuments = components.concat(metaPdfs);

    this.components = buildHierarchyForCategory(components);
    this.metaPdfs = buildHierarchyForCategory(metaPdfs);
    this.documents = new Map(allDocuments.map((document) => [document.id, document] as [number, SanitizedDocument]));
  }

  getDocuments(): Map<number, number[]> {
    return new Map([...this.components, ...this.metaPdfs]);
  }

  getName(documentId: number) {
    return this.documents.get(documentId)!.name;
  }

  getPagesInCategory(category: DocumentCategory): number[] {
    return Array.from(this[category].values()).flat();
  }

  getPagesInDocument(documentId: number) {
    return this.getDocuments().get(documentId)!;
  }

  getFirstPageOfEveryComponent(): number[] {
    return Array.from(this.components.keys()).map(this.getFirstPage.bind(this));
  }

  getFirstPageOfEveryMetaPdf(): number[] {
    return Array.from(this.metaPdfs.keys()).map(this.getFirstPage.bind(this));
  }

  getEveryPage(): number[] {
    return Array.from(this.documents.values()).flatMap((document) => document.pdfPages.map((page) => page.id));
  }

  private getFirstPage(documentId: number): number {
    return this.documents.get(documentId)!.pdfPages[0].id;
  }
}
